import "./Thumbnail.scss";
import classNames from "classnames";

import { CaseSlide, Slide } from "pwapi";

import { useViewableSlideStatuses } from "shared/hooks/slideHooks";
import { lesionLineTypeByName } from "shared/reducers/aiReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";
import { slideStainLabel } from "shared/utils/slideUtils";
import { SortedSlideBase } from "shared/utils/slideUtils";

import { ReactComponent as LogoSVG } from "images/logo.svg";
import { selectSpecimenClassification } from "selectors/classificationSelector";
import { useAppSelector } from "store/hooks";

import { SlideFlagIcon } from "./SlideFlagIcon";

// type ThumbnailSlide = Omit<CaseSlide, "stain"> & SortedSlideBase;
type ThumbnailSlide = CaseSlide & SortedSlideBase;

interface ThumbnailProps {
  specimen?: string;
  highlighted?: boolean;
  viewed?: boolean;
  completed?: boolean;
  diagnostic?: boolean;
  flagged?: boolean;
  archived?: boolean;
  rotation?: number;
  slide: ThumbnailSlide;
}

const Thumbnail = (props: ThumbnailProps) => {
  const stain = useAppSelector(
    ({ stains }) => stains[props.slide.stain_id || 0]
  );
  const features: FeatureFlags = useAppSelector(({ sys }) => sys.features);

  const specimenClassification = useAppSelector((state) =>
    selectSpecimenClassification(state, props.slide?.specimen_id)
  );

  // TODO: "hasBeenRevealed" is probably not accurate to "maskLesionLineType"
  // these should be separated out
  // [ specimen_id ] -> maskLesionLineType -- it's per specimen, not per slide anymore
  // (except when we are viewing a single slide outside of the context of a case)
  const hasBeenRevealed = useAppSelector(({ ai }) => ai.maskLesionLineType);

  const hasSpecimenClassification =
    features.prospectiveAI && !!specimenClassification?.length;

  const src = props.slide.thumbnail;

  const positiveClassifications = specimenClassification
    ?.find((c) => c?.slide_id === props.slide.id)
    ?.lesion_lines?.filter((l) => l.diagnosis_result === "POSITIVE");

  const inconclusiveClassifications = specimenClassification
    ?.find((c) => c?.slide_id === props.slide.id)
    ?.lesion_lines?.filter((l) => l.diagnosis_result === "INCONCLUSIVE");

  const viewableSlideStatuses = useViewableSlideStatuses();
  const viewableSlide = viewableSlideStatuses.includes(
    props.slide.status || Slide.status.ERROR
  );

  return (
    <figure
      className={classNames("SlideThumb", "Thumbnail", {
        "Thumbnail--highlighted": props.highlighted,
      })}
    >
      <div className="Thumbnail__bubbles">
        {!props.viewed && <span className="Thumbnail__not__viewed" />}
        {props.flagged && (
          <span className="Thumbnail__flagged">
            <SlideFlagIcon />
          </span>
        )}
      </div>

      {!props.archived && viewableSlide && (
        <img src={src} alt="" className="Thumbnail__image" />
      )}
      {!props.archived && !viewableSlide && (
        <figcaption className="Thumbnail__pending">PENDING</figcaption>
      )}

      <figcaption className="Thumbnail__label">
        {props.slide.full_name}
      </figcaption>
      {(props.slide.is_control ||
        props.slide.is_rescan ||
        stain?.abbreviation !== "H&E") && (
        <figcaption className="Thumbnail__stain">
          {slideStainLabel(props.slide as CaseSlide, stain)}
        </figcaption>
      )}

      {hasSpecimenClassification &&
        (!!positiveClassifications?.length ||
          !!inconclusiveClassifications?.length) && (
          <div
            className={classNames("Thumbnail__ai", {
              "Thumbnail__ai--positive": positiveClassifications?.length,
              "Thumbnail__ai--inconclusive":
                inconclusiveClassifications?.length,
              "Thumbnail__ai--revealed": hasBeenRevealed,
            })}
          >
            <LogoSVG />{" "}
            {hasBeenRevealed && (
              <>
                {!!positiveClassifications?.length &&
                  positiveClassifications?.map((c) => (
                    <span key={c.id} className="Thumbnail__ai__label--positive">
                      {lesionLineTypeByName(c.diagnosis_name)}
                    </span>
                  ))}
                {!positiveClassifications?.length &&
                  !!inconclusiveClassifications?.length &&
                  inconclusiveClassifications?.map((c) => (
                    <span
                      key={c.id}
                      className="Thumbnail__ai__label--inconclusive"
                    >
                      {lesionLineTypeByName(c.diagnosis_name)}
                    </span>
                  ))}
              </>
            )}
          </div>
        )}
    </figure>
  );
};

export default Thumbnail;
